import { motion } from 'framer-motion';
import { Shield, ChevronDown, MessageCircle } from 'lucide-react';

const LOGO_URL = "/assets/images/hero-logo.png";
const HERO_BG = "/assets/images/hero-bg.jpg";
const HERO_BG_MOBILE = "/assets/images/hero-bg-mobile.jpg";
const WHATSAPP_NUMBER = "+41779938989";

export const Hero = ({ onOpenWizard }) => {
  const openWhatsApp = () => {
    const message = encodeURIComponent("Guten Tag, ich interessiere mich für Ihre Sicherheitsdienstleistungen.");
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${message}`, '_blank');
  };

  const scrollToServices = () => {
    document.querySelector('#leistungen')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="start"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-0"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <picture>
          <source media="(max-width: 768px)" srcSet={HERO_BG_MOBILE} />
          <img
            src={HERO_BG}
            alt="Security professionals in action"
            className="w-full h-full object-cover opacity-45"
          />
        </picture>
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 text-center">
        {/* Logo Animation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-8 md:mb-12"
        >
          <img
            src={LOGO_URL}
            alt="Praesidium Protection"
            className="h-48 md:h-60 lg:h-64 w-auto mx-auto float-animation -mt-20 md:mt-0"
            data-testid="hero-logo"
          />
        </motion.div>

        {/* Tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex items-center justify-center gap-3 mb-6"
        >
          <div className="divider" />
          <span className="text-[#94A3B8] uppercase tracking-[0.3em] text-xs md:text-sm font-medium">
            Schweizer Sicherheitsdienst - diskret & professionell
          </span>
          <div className="divider" />
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-[#F1F5F9] mb-6 leading-tight"
          data-testid="hero-headline"
        >
          Sicherheit, die man spürt -{' '}
          <span className="gold-text">nicht sieht.</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto mb-10 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Schweizer Sicherheitsdienst für Personen, Veranstaltungen, Zutritt und Objekte. Wir arbeiten ruhig, strukturiert und mit hoher Vertraulichkeit. In der Schweiz – und bei Bedarf auch darüber hinaus.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={onOpenWizard}
            className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
            data-testid="hero-wizard-btn"
          >
            <Shield size={18} />
            60-Sekunden Anfrage
          </button>
          <button
            onClick={openWhatsApp}
            className="btn-secondary w-full sm:w-auto flex items-center justify-center gap-2"
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle size={18} />
            Direkt via WhatsApp
          </button>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.3 }}
          onClick={scrollToServices}
          className="mt-10 text-[#64748B] hover:text-[#D4AF37] transition-colors cursor-pointer"
          data-testid="scroll-indicator"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown size={32} />
          </motion.div>
        </motion.button>
      </div>
    </section>
  );
};
